/**
 * Valores em dinheiro SEM ponto flutuante: centavos em bigint.
 * Mesma precisão do banco (numeric(14,2)); arredondamento meio para cima.
 */
import { QUANTITY_SCALE, toScaled } from "./quantities";

const CENTS_PER_SCALED = 10n ** BigInt(QUANTITY_SCALE - 2);

/** "1234.5", "1234,56", 10 → centavos. Mais de 2 casas é arredondado (meio para cima). */
export function toCents(value: string | number): bigint {
  const scaled = toScaled(value);
  const neg = scaled < 0n;
  const abs = neg ? -scaled : scaled;
  const cents = (abs + CENTS_PER_SCALED / 2n) / CENTS_PER_SCALED;
  return neg ? -cents : cents;
}

/** Centavos → "1234.56" (sempre 2 casas, formato do banco/JSON). */
export function fromCents(cents: bigint): string {
  const neg = cents < 0n;
  const abs = neg ? -cents : cents;
  return `${neg ? "-" : ""}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`;
}

/** Normaliza para 2 casas ("10" → "10.00"). */
export const money = (value: string | number): string => fromCents(toCents(value));

export function sumMoney(values: (string | number)[]): string {
  return fromCents(values.reduce<bigint>((acc, v) => acc + toCents(v), 0n));
}

export function subtractMoney(a: string | number, b: string | number): string {
  return fromCents(toCents(a) - toCents(b));
}

export function compareMoney(a: string | number, b: string | number): number {
  const x = toCents(a);
  const y = toCents(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/** Valor da linha: quantidade × preço unitário, em centavos arredondados. */
export function lineValue(quantity: string | number, unitPrice: string | number): string {
  const product = toScaled(quantity) * toScaled(unitPrice); // escala 10^12
  const perCent = 10n ** BigInt(QUANTITY_SCALE * 2 - 2);
  const neg = product < 0n;
  const abs = neg ? -product : product;
  const cents = (abs + perCent / 2n) / perCent;
  return fromCents(neg ? -cents : cents);
}

/**
 * Divide um valor em N partes que somam exatamente o total.
 * Os centavos que sobram vão para as primeiras partes (100 ÷ 3 = 33,34 + 33,33 + 33,33).
 */
export function splitMoney(total: string | number, parts: number): string[] {
  if (!Number.isInteger(parts) || parts < 1) throw new Error("Quantidade de partes inválida");
  const cents = toCents(total);
  if (cents < 0n) throw new Error("Valor negativo");
  const n = BigInt(parts);
  const base = cents / n;
  const extra = cents % n;
  return Array.from({ length: parts }, (_, i) => fromCents(base + (BigInt(i) < extra ? 1n : 0n)));
}

/**
 * Rateio proporcional (maiores restos): distribui `total` conforme os pesos,
 * somando exatamente o total. Pesos todos zero → partes iguais.
 * Os pesos usam 6 casas (servem para valores e para quantidades, ex.: lotes com
 * 3 casas). Total ou peso negativo é recusado.
 * Usado para ratear frete/seguro/outras despesas pelo valor dos produtos.
 */
export function apportionMoney(total: string | number, weights: (string | number)[]): string[] {
  if (weights.length === 0) return [];
  const cents = toCents(total);
  if (cents < 0n) throw new Error("Valor negativo");
  const w = weights.map((x) => toScaled(x));
  if (w.some((x) => x < 0n)) throw new Error("Peso negativo");
  const sum = w.reduce((a, b) => a + b, 0n);
  if (sum === 0n) return splitMoney(fromCents(cents), weights.length);
  const raw = w.map((x) => x * cents);
  const shares = raw.map((r) => r / sum);
  let rest = cents - shares.reduce((a, b) => a + b, 0n);
  const order = raw
    .map((r, i) => ({ i, rem: r % sum }))
    .sort((a, b) => (a.rem === b.rem ? a.i - b.i : a.rem > b.rem ? -1 : 1));
  for (const o of order) {
    if (rest <= 0n) break;
    shares[o.i] = shares[o.i]! + 1n;
    rest -= 1n;
  }
  return shares.map(fromCents);
}

/**
 * Rateio proporcional com teto por parte (ex.: desconto de cada lote ≤ valor do lote).
 * Rateia como `apportionMoney` e, se alguma parte passar do teto, leva o excedente para
 * as partes com folga (a maior folga primeiro). A soma continua exata.
 */
export function apportionMoneyCapped(
  total: string | number,
  weights: (string | number)[],
  caps: (string | number)[],
): string[] {
  if (caps.length !== weights.length) throw new Error("Tetos e pesos em quantidades diferentes");
  const capCents = caps.map((c) => toCents(c));
  const totalCents = toCents(total);
  if (totalCents > capCents.reduce((a, b) => a + b, 0n))
    throw new Error("Valor maior que os tetos");
  const shares = apportionMoney(total, weights).map((v) => toCents(v));
  let excess = 0n;
  for (let i = 0; i < shares.length; i++) {
    if (shares[i]! > capCents[i]!) {
      excess += shares[i]! - capCents[i]!;
      shares[i] = capCents[i]!;
    }
  }
  const bySlack = shares
    .map((v, i) => ({ i, slack: capCents[i]! - v }))
    .sort((a, b) => (a.slack === b.slack ? a.i - b.i : a.slack > b.slack ? -1 : 1));
  for (const o of bySlack) {
    if (excess <= 0n) break;
    const move = o.slack < excess ? o.slack : excess;
    shares[o.i] = shares[o.i]! + move;
    excess -= move;
  }
  return shares.map(fromCents);
}

/** Data ISO (AAAA-MM-DD) + dias, sem fuso horário. */
export function addDays(isoDate: string, days: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) throw new Error("Data inválida");
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) throw new Error("Data inválida");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export type InstallmentDraft = { number: number; dueDate: string; amount: string };

/**
 * Parcelas iguais: 1ª em `firstDueDays` dias a partir da data base, as demais a cada
 * `intervalDays` dias. Soma sempre igual ao total.
 */
export function buildInstallments(opts: {
  total: string | number;
  count: number;
  baseDate: string;
  firstDueDays: number;
  intervalDays: number;
}): InstallmentDraft[] {
  return splitMoney(opts.total, opts.count).map((amount, i) => ({
    number: i + 1,
    dueDate: addDays(opts.baseDate, opts.firstDueDays + i * opts.intervalDays),
    amount,
  }));
}

/** R$ 1.234,56 */
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const s = money(value);
  const neg = s.startsWith("-");
  const [int, frac] = (neg ? s.slice(1) : s).split(".");
  return `${neg ? "-" : ""}R$ ${int!.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${frac}`;
}
